import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import '../../services/GlobalVariables';
import axios from 'axios';

const API_URL = global.SET_API_URL + 'deals/';

const initialState = {
    deals: [],
    activeDeals: [],
    selectedDeal: null,
    isLoading: false,
    isError: false,
    isSuccess: false,
    message: ''
};

// Get all deals (for admin management)
export const fetchAllDeals = createAsyncThunk(
    'deals/fetchAll',
    async (_, thunkAPI) => {
        try {
            const state = thunkAPI.getState();
            const branchID = state.auth?.user?.defaultBranchID || 1;
            const res = await axios.post(API_URL + 'getAllDeals', { branchID });
            return res.data.data || [];
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Get active deals (for POS)
export const fetchActiveDeals = createAsyncThunk(
    'deals/fetchActive',
    async (_, thunkAPI) => {
        try {
            const state = thunkAPI.getState();
            const branchID = state.auth?.user?.defaultBranchID || 1;
            const res = await axios.post(API_URL + 'getActiveDeals', { branchID });
            return res.data.data || [];
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Get deal by ID (with items)
export const fetchDealById = createAsyncThunk(
    'deals/fetchById',
    async (dealID, thunkAPI) => {
        try {
            const res = await axios.post(API_URL + 'getDealById', { dealID });
            return res.data.data || null;
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Create new deal
export const createDeal = createAsyncThunk(
    'deals/create',
    async (payload, thunkAPI) => {
        try {
            const res = await axios.post(API_URL + 'createDeal', payload);
            return res.data;
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Update deal
export const updateDeal = createAsyncThunk(
    'deals/update',
    async (payload, thunkAPI) => {
        try {
            const res = await axios.post(API_URL + 'updateDeal', payload);
            return res.data;
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Delete deal
export const deleteDeal = createAsyncThunk(
    'deals/delete',
    async (dealID, thunkAPI) => {
        try {
            const res = await axios.post(API_URL + 'deleteDeal', { dealID });
            return res.data;
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Toggle deal status (active/inactive)
export const toggleDealStatus = createAsyncThunk(
    'deals/toggleStatus',
    async (payload, thunkAPI) => {
        try {
            const res = await axios.post(API_URL + 'toggleDealStatus', payload);
            return res.data;
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

const dealsSlice = createSlice({
    name: 'deals',
    initialState,
    reducers: {
        reset: (state) => {
            state.isLoading = false;
            state.isError = false;
            state.isSuccess = false;
            state.message = '';
        },
        clearSelectedDeal: (state) => {
            state.selectedDeal = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch All Deals
            .addCase(fetchAllDeals.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchAllDeals.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.deals = action.payload;
            })
            .addCase(fetchAllDeals.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            // Fetch Active Deals
            .addCase(fetchActiveDeals.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchActiveDeals.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.activeDeals = action.payload;
            })
            .addCase(fetchActiveDeals.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            // Fetch Deal By ID
            .addCase(fetchDealById.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchDealById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.selectedDeal = action.payload;
            })
            .addCase(fetchDealById.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            // Create Deal
            .addCase(createDeal.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createDeal.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.message = action.payload.message;
            })
            .addCase(createDeal.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            // Update Deal
            .addCase(updateDeal.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateDeal.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.message = action.payload.message;
            })
            .addCase(updateDeal.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            // Delete Deal
            .addCase(deleteDeal.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteDeal.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.message = action.payload.message;
            })
            .addCase(deleteDeal.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            // Toggle Deal Status
            .addCase(toggleDealStatus.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(toggleDealStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.message = action.payload.message;
            })
            .addCase(toggleDealStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            });
    }
});

export const { reset, clearSelectedDeal } = dealsSlice.actions;
export default dealsSlice.reducer;
